import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import CreateDepartmentWithTeams from '../components/forms/CreateDepartmentWithTeams';

import StatusBadge from '../components/ui/StatusBadge';
import CapacityBar, { CapacityBarMini } from '../components/ui/CapacityBar';
import { 
  Users, 
  Plus,
  Search,
  Filter,
  Calendar,
  CheckSquare,
  User,
  FolderOpen,
  X,
  Edit,
  Trash2,
  Building2,
  Clock,
  Check  // הוסף את זה אם חסר
} from 'lucide-react';
import { db, COLLECTIONS } from '../firebase/config';
import { collection, addDoc, serverTimestamp, updateDoc, doc, deleteDoc } from 'firebase/firestore';
const EmployeeSearchField = ({ 
  employees = [], 
  selectedEmployees = [], 
  onEmployeeSelect,
  onEmployeeRemove,
  placeholder = "חפש עובדים...",
  multiple = true,
  showDepartment = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const dropdownRef = useRef(null);

  // Filter employees based on search term
  useEffect(() => {
    if (showAll) {
      // Show all employees when "show all" is active
      const allAvailable = employees.filter(emp => !selectedEmployees.find(selected => selected.id === emp.id));
      setFilteredEmployees(allAvailable);
      return;
    }
    
    if (searchTerm.trim() === '') {
      setFilteredEmployees([]);
      return;
    }

    const filtered = employees.filter(employee => {
      const searchLower = searchTerm.toLowerCase();
      return (
        employee.name?.toLowerCase().includes(searchLower) ||
        employee.email?.toLowerCase().includes(searchLower) ||
        employee.role?.toLowerCase().includes(searchLower)
      );
    }).filter(emp => !selectedEmployees.find(selected => selected.id === emp.id));

    setFilteredEmployees(filtered);
  }, [searchTerm, employees, selectedEmployees, showAll]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowAll(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmployeeSelect = (employee) => {
    onEmployeeSelect(employee);
    if (!showAll) {
      setSearchTerm('');
      setIsOpen(false);
    }
    if (!multiple) {
      setFilteredEmployees([]);
      setShowAll(false);
    }
  };

  const handleRemoveEmployee = (employeeId) => {
    onEmployeeRemove(employeeId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Employees */}
      {selectedEmployees.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedEmployees.map(employee => (
            <div
              key={employee.id}
              className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
            >
              <User size={14} />
              <span>{employee.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveEmployee(employee.id)}
                className="hover:text-blue-600 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsOpen(true);
              setShowAll(false);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setShowAll(!showAll);
            setIsOpen(!showAll);
            setSearchTerm('');
          }}
          className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
            showAll 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {showAll ? 'סגור' : 'הצג הכל'}
        </button>
      </div>

      {/* Dropdown Results */}
      {isOpen && (searchTerm || showAll) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {showAll && filteredEmployees.length > 0 && (
            <div className="sticky top-0 bg-blue-50 px-4 py-2 border-b border-blue-200 flex items-center justify-between">
              <p className="text-sm font-medium text-blue-900">
                עובדים זמינים: {filteredEmployees.length}
              </p>
              {multiple && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    filteredEmployees.forEach(emp => handleEmployeeSelect(emp));
                  }}
                  className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
                >
                  בחר הכל
                </button>
              )}
            </div>
          )}
          {filteredEmployees.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 text-center">
              לא נמצאו עובדים
            </div>
          ) : (
            filteredEmployees.map(employee => (
              <button
                key={employee.id}
                type="button"
                onClick={() => handleEmployeeSelect(employee)}
                className="w-full px-4 py-3 hover:bg-blue-50 flex items-center justify-between transition-colors text-right border-b last:border-b-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User size={16} className="text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{employee.name}</div>
                    <div className="text-sm text-gray-500">{employee.email}</div>
                    {employee.role && (
                      <div className="text-xs text-gray-400">
                        {(() => {
                          switch (employee.role) {
                            case 'org_admin':
                              return 'מנהל ראשי';
                            case 'ceo':
                              return 'מנכ"ל';
                            case 'deputy_ceo':
                              return 'סמנכ"ל';
                            case 'dept_manager':
                              return 'מנהל מחלקה';
                            case 'team_manager':
                              return 'מנהל צוות';
                            case 'employee':
                              return 'עובד';
                            default:
                              return employee.role;
                          }
                        })()}
                      </div>
                    )}
                    {showDepartment && employee.departmentName && (
                      <div className="text-xs text-blue-500">{employee.departmentName}</div>
                    )}
                  </div>
                </div>
                {selectedEmployees.find(e => e.id === employee.id) && (
                  <Check size={16} className="text-green-600" />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const Departments = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { departments, users, tasks, projects, teams, updateUser, createDepartment, updateDepartment, deleteDepartment } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateWithTeams, setShowCreateWithTeams] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    description: '',
    managerIds: [],
    teamMembers: [],
    isActive: true
  });

  // Filter departments based on user role, search and filters
  const filteredDepartments = departments.filter(department => {
    // First filter by user role
    let hasAccess = false;
    
    if (currentUser?.role === 'org_admin') {
      // Org admin sees all departments
      hasAccess = true;
    } else if (currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo') {
      // CEO and Deputy CEO see all departments created by CEO, Deputy CEO, or org_admin
      hasAccess = department.createdBy === currentUser.id || 
                  users.find(u => u.id === department.createdBy)?.role === 'ceo' ||
                  users.find(u => u.id === department.createdBy)?.role === 'deputy_ceo' ||
                  users.find(u => u.id === department.createdBy)?.role === 'org_admin';
    } else if (currentUser?.role === 'dept_manager') {
      // Department manager sees their own department(s) and departments they created - תמיכה במספר מחלקות
      const userDeptIds = Array.isArray(currentUser.departmentIds) ? currentUser.departmentIds : (currentUser.departmentId ? [currentUser.departmentId] : []);
      hasAccess = userDeptIds.includes(department.id) || 
                  department.createdBy === currentUser.id ||
                  department.managerId === currentUser.id;
    } else {
      // Employees don't have access to departments page, but if they do, show only their department(s) - תמיכה במספר מחלקות
      const userDeptIds = Array.isArray(currentUser?.departmentIds) ? currentUser.departmentIds : (currentUser?.departmentId ? [currentUser.departmentId] : []);
      hasAccess = userDeptIds.includes(department.id);
    }
    
    if (!hasAccess) return false;
    
    // Then apply search and status filters
    const matchesSearch = department.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         department.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && department.isActive) ||
                         (filterStatus === 'inactive' && !department.isActive);
    
    return matchesSearch && matchesStatus;
  });

  // Reset department form
  const resetDepartmentForm = () => {
    setDepartmentForm({
      name: '',
      description: '',
      managerIds: [],
      teamMembers: [],
      isActive: true
    });
  };

  // Handle create department
  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    try {
      const departmentData = {
        name: departmentForm.name,
        description: departmentForm.description,
        managerIds: departmentForm.managerIds,
        teamMembers: departmentForm.teamMembers,
        isActive: departmentForm.isActive ?? true
      };
      
      // Use createDepartment from DataContext which handles teamMembers automatically
      const newDepartmentId = await createDepartment(departmentData);
      
      // If managers are specified, assign them to the department (תמיכה במספר מחלקות)
      if (departmentForm.managerIds && departmentForm.managerIds.length > 0) {
        for (const managerId of departmentForm.managerIds) {
          try {
            if (updateUser) {
              const manager = users.find(u => u.id === managerId);
              const currentDeptIds = Array.isArray(manager?.departmentIds) ? manager.departmentIds : (manager?.departmentId ? [manager.departmentId] : []);
              const newDeptIds = [...new Set([...currentDeptIds, newDepartmentId])];
              await updateUser(managerId, { 
                departmentIds: newDeptIds, 
                departmentId: newDeptIds[0] // backward compatibility
              });
            }
          } catch (updateError) {
            console.error('Error assigning manager to department:', updateError);
          }
        }
      }
      // If current user is a dept_manager and they created a department without specifying managers, update their departmentId
      else if (currentUser.role === 'dept_manager') {
        try {
          // Update the current user's departmentId to the new department (תמיכה במספר מחלקות)
          if (updateUser) {
            const currentDeptIds = Array.isArray(currentUser.departmentIds) ? currentUser.departmentIds : (currentUser.departmentId ? [currentUser.departmentId] : []);
            const newDeptIds = [...new Set([...currentDeptIds, newDepartmentId])];
            await updateUser(currentUser.id, { 
              departmentIds: newDeptIds, 
              departmentId: newDeptIds[0] // backward compatibility
            });
          }
        } catch (updateError) {
          console.error('Error updating user department:', updateError);
        }
      }
      
      setShowDepartmentModal(false);
      resetDepartmentForm();
      
      // Show success message
      if (departmentForm.managerIds && departmentForm.managerIds.length > 0) {
        const managerNames = departmentForm.managerIds.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`המחלקה "${departmentForm.name}" נוצרה בהצלחה! ${managerNames} מוגדרים כמנהלי המחלקה.`);
      } else if (currentUser.role === 'dept_manager') {
        alert(`המחלקה "${departmentForm.name}" נוצרה בהצלחה! אתה מוגדר כמנהל המחלקה החדשה.`);
      } else {
        alert(`המחלקה "${departmentForm.name}" נוצרה בהצלחה!`);
      }
      
      // הוסף הודעה על העובדים שנוספו
      if (departmentForm.teamMembers && departmentForm.teamMembers.length > 0) {
        const teamMemberNames = departmentForm.teamMembers.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`העובדים הבאים נוספו למחלקה: ${teamMemberNames}`);
      }
    } catch (error) {
      console.error('Error creating department:', error);
      alert('שגיאה ביצירת המחלקה');
    }
  };

  // Handle edit department
  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    setDepartmentForm({
      name: department.name,
      description: department.description || '',
      managerIds: department.managerIds || (department.managerId ? [department.managerId] : []),
      teamMembers: department.teamMembers || [],
      isActive: department.isActive ?? true
    });
    setShowEditModal(true);
  };

  // Handle update department
  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    try {
      const departmentData = {
        name: departmentForm.name,
        description: departmentForm.description,
        managerIds: departmentForm.managerIds,
        teamMembers: departmentForm.teamMembers,
        isActive: departmentForm.isActive
      };
      
      // Get the previous manager IDs to compare
      const previousManagerIds = editingDepartment.managerIds || (editingDepartment.managerId ? [editingDepartment.managerId] : []);
      const newManagerIds = departmentForm.managerIds || [];
      
      // Use updateDepartment from DataContext which handles teamMembers automatically
      await updateDepartment(editingDepartment.id, departmentData);
      
      // Handle manager changes (תמיכה במספר מחלקות)
      const managersToRemove = previousManagerIds.filter(id => !newManagerIds.includes(id));
      const managersToAdd = newManagerIds.filter(id => !previousManagerIds.includes(id));
      
      // Remove previous managers' department assignment (הסרה מהמערך)
      for (const managerId of managersToRemove) {
        try {
          if (updateUser) {
            const manager = users.find(u => u.id === managerId);
            const currentDeptIds = Array.isArray(manager?.departmentIds) ? manager.departmentIds : (manager?.departmentId ? [manager.departmentId] : []);
            const newDeptIds = currentDeptIds.filter(id => id !== editingDepartment.id);
            await updateUser(managerId, { 
              departmentIds: newDeptIds, 
              departmentId: newDeptIds.length > 0 ? newDeptIds[0] : null // backward compatibility
            });
          }
        } catch (error) {
          console.error('Error removing manager from department:', error);
        }
      }
      
      // Assign new managers to this department (הוספה למערך)
      for (const managerId of managersToAdd) {
        try {
          if (updateUser) {
            const manager = users.find(u => u.id === managerId);
            const currentDeptIds = Array.isArray(manager?.departmentIds) ? manager.departmentIds : (manager?.departmentId ? [manager.departmentId] : []);
            const newDeptIds = [...new Set([...currentDeptIds, editingDepartment.id])];
            await updateUser(managerId, { 
              departmentIds: newDeptIds, 
              departmentId: newDeptIds[0] // backward compatibility
            });
          }
        } catch (error) {
          console.error('Error assigning manager to department:', error);
        }
      }
      
      setShowEditModal(false);
      setEditingDepartment(null);
      resetDepartmentForm();
      alert('המחלקה עודכנה בהצלחה!');
      
      // הוסף הודעה על העובדים שעודכנו
      const previousTeamMembers = editingDepartment.teamMembers || [];
      const newTeamMembers = departmentForm.teamMembers || [];
      const addedMembers = newTeamMembers.filter(id => !previousTeamMembers.includes(id));
      const removedMembers = previousTeamMembers.filter(id => !newTeamMembers.includes(id));
      
      if (addedMembers.length > 0) {
        const addedNames = addedMembers.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`העובדים הבאים נוספו למחלקה: ${addedNames}`);
      }
      
      if (removedMembers.length > 0) {
        const removedNames = removedMembers.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`העובדים הבאים הוסרו מהמחלקה: ${removedNames}`);
      }
    } catch (error) {
      console.error('Error updating department:', error);
      alert('שגיאה בעדכון המחלקה');
    }
  };

  // Handle delete department
  const handleDeleteDepartment = async (department) => {
    // Check if department has teams
    const departmentTeams = teams.filter(team => team.departmentId === department.id);
    // תמיכה במספר מחלקות - משתמש עדיין שייך למחלקה אם היא באחת מהמחלקות שלו
    const departmentUsers = users.filter(user => {
      const userDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
      return userDeptIds.includes(department.id);
    });
    const departmentTasks = tasks.filter(task => task.departmentId === department.id);
    const departmentProjects = projects.filter(project => project.departmentId === department.id);
    
    let confirmMessage = `האם אתה בטוח שברצונך למחוק את המחלקה "${department.name}"?\n\n`;
    
    if (departmentUsers.length > 0) {
      confirmMessage += `⚠️ המחלקה מכילה ${departmentUsers.length} עובדים שיוסרו מהמחלקה.\n`;
    }
    
    if (departmentTeams.length > 0) {
      confirmMessage += `⚠️ המחלקה מכילה ${departmentTeams.length} צוותים.\n`;
    }
    
    if (departmentTasks.length > 0) {
      confirmMessage += `⚠️ יש ${departmentTasks.length} משימות קשורות למחלקה.\n`;
    }
    
    if (departmentProjects.length > 0) {
      confirmMessage += `⚠️ יש ${departmentProjects.length} פרויקטים קשורים למחלקה.\n`;
    }
    
    confirmMessage += `\nפעולה זו לא ניתנת לביטול!`;
    
    if (window.confirm(confirmMessage)) {
      try {
        // Use deleteDepartment from DataContext which handles removing departmentId from users
        await deleteDepartment(department.id);
        
        // Create success message
        let successMessage = `המחלקה "${department.name}" נמחקה בהצלחה!`;
        
        if (departmentUsers.length > 0) {
          successMessage += `\n\n${departmentUsers.length} עובדים הוסרו מהמחלקה.`;
        }
        
        if (departmentTeams.length > 0) {
          successMessage += `\n\n${departmentTeams.length} צוותים נשארו ללא מחלקה.`;
        }
        
        alert(successMessage);
      } catch (error) {
        console.error('Error deleting department:', error);
        alert('שגיאה במחיקת המחלקה. נסה שוב.');
      }
    }
  };

  // Calculate department statistics
  const getDepartmentStats = (department) => {
    // תמיכה במספר מחלקות - משתמש נחשב בסטטיסטיקה אם המחלקה היא אחת ממחלקותיו
    const deptUsers = users.filter(u => {
      const userDeptIds = Array.isArray(u.departmentIds) ? u.departmentIds : (u.departmentId ? [u.departmentId] : []);
      return userDeptIds.includes(department.id);
    });
    const deptTasks = tasks.filter(t => t.departmentId === department.id);
    const deptProjects = projects.filter(p => p.departmentId === department.id);
    
    return {
      userCount: deptUsers.length,
      taskCount: deptTasks.length,
      projectCount: deptProjects.length,
      completedTasks: deptTasks.filter(t => t.status === 'הושלם').length,
      activeTasks: deptTasks.filter(t => t.status !== 'הושלם').length
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">מחלקות הארגון</h1>
              <p className="text-lg text-gray-600">ניהול ופיקוח על כל מחלקות החברה</p>
            </div>
            {(currentUser?.role === 'org_admin' || currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'dept_manager') && (
              <div className="flex gap-4">
                <button
                  onClick={() => setShowCreateWithTeams(true)}
                  className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Plus size={20} />
                  מחלקה עם צוותים
                </button>
                <button
                  onClick={() => {
                    setShowDepartmentModal(true);
                  }}
                  className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Plus size={20} />
                  מחלקה פשוטה
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap gap-6">
            {/* Search */}
            <div className="flex-1 min-w-[300px]">
              <label className="block text-sm font-semibold text-gray-700 mb-2">חיפוש מחלקות</label>
              <div className="relative">
                <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="חפש לפי שם מחלקה, מנהל או תיאור..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-12 pl-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="min-w-[200px]">
              <label className="block text-sm font-semibold text-gray-700 mb-2">סטטוס</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              >
                <option value="all">כל הסטטוסים</option>
                <option value="active">פעילות</option>
                <option value="inactive">לא פעילות</option>
              </select>
            </div>
          </div>
        </div>

        {/* Departments Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {filteredDepartments.map(department => {
            const stats = getDepartmentStats(department);
            
            return (
              <div
                key={department.id}
                onClick={() => navigate(`/departments/${department.id}`)}
                className="group relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer p-8 border border-gray-100 hover:border-blue-300 hover:scale-[1.03] overflow-hidden"
              >
                {/* Elegant Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/20 to-purple-100/20 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700"></div>
                
                {/* Status Badge */}
                <div className="absolute top-6 right-6 z-10">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-md whitespace-nowrap ${
                    department.isActive 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white' 
                      : 'bg-gradient-to-r from-red-500 to-pink-600 text-white'
                  }`}>
                    {department.isActive ? 'פעילה' : 'לא פעילה'}
                  </span>
                </div>

                {/* Action buttons - for org_admin, ceo, deputy_ceo */}
                {(currentUser?.role === 'org_admin' || currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo') && (
                  <div className="absolute top-6 left-6 flex gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20">
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditDepartment(department);
                      }}
                      className="p-3 text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:scale-110 rounded-xl transition-all duration-300 flex-shrink-0 shadow-lg hover:shadow-xl"
                      title="ערוך מחלקה"
                      type="button"
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteDepartment(department);
                      }}
                      className="p-3 text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 hover:scale-110 rounded-xl transition-all duration-300 flex-shrink-0 shadow-lg hover:shadow-xl"
                      title="מחק מחלקה"
                      type="button"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                )}

                {/* Header */}
                <div className="relative z-10 mb-8 pt-16">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-slate-600 to-blue-700 rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Building2 size={24} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-2xl text-gray-900 mb-2 group-hover:text-blue-700 transition-colors duration-300 break-words">
                        {department.name}
                      </h3>
                      <p className="text-gray-600 text-base leading-relaxed line-clamp-3">
                        {department.description || 'אין תיאור זמין למחלקה זו'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="relative z-10 space-y-6">
                  {/* Manager Info */}
                  <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-4 border border-slate-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                        <User size={16} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-700 mb-1">מנהלי המחלקה</p>
                        <p className="text-sm text-slate-600">
                          {(() => {
                            const managerIds = department.managerIds || (department.managerId ? [department.managerId] : []);
                            if (managerIds.length === 0) return 'לא מוגדרים';
                            
                            const managerNames = managerIds.map(id =>
                              users.find(u => u.id === id)?.name || 'לא ידוע'
                            ).join(', ');
                            
                            return managerNames;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-4 border border-emerald-200 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                          <Users size={18} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-2xl font-bold text-emerald-700">{stats.userCount}</p>
                          <p className="text-sm text-emerald-600 font-medium">עובדים</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-4 border border-purple-200 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-violet-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                          <CheckSquare size={18} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-2xl font-bold text-purple-700">{stats.taskCount}</p>
                          <p className="text-sm text-purple-600 font-medium">משימות</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-4 border border-orange-200 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                          <FolderOpen size={18} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-2xl font-bold text-orange-700">{stats.projectCount}</p>
                          <p className="text-sm text-orange-600 font-medium">פרויקטים</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-4 border border-teal-200 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                          <Calendar size={18} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-2xl font-bold text-teal-700">{stats.completedTasks}</p>
                          <p className="text-sm text-teal-600 font-medium">הושלמו</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {stats.taskCount > 0 && (
                    <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-4 border border-slate-200">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-semibold text-slate-700">התקדמות משימות</span>
                        <span className="text-sm font-bold text-blue-600">{stats.completedTasks}/{stats.taskCount}</span>
                      </div>
                      <CapacityBarMini 
                        current={stats.completedTasks}
                        total={stats.taskCount}
                      />
                    </div>
                  )}

                  {/* Active Tasks */}
                  {stats.activeTasks > 0 && (
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-4 border border-orange-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                          <Clock size={16} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-orange-700">{stats.activeTasks} משימות פעילות</p>
                          <p className="text-xs text-orange-600">דורשות טיפול</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>


              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </div>
          );
        })}
      </div>

        {/* Empty State */}
        {filteredDepartments.length === 0 && (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gradient-to-br from-slate-100 to-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Building2 size={40} className="text-slate-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {searchTerm || filterStatus !== 'all' ? 'לא נמצאו מחלקות' : 'אין מחלקות עדיין'}
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
              {searchTerm || filterStatus !== 'all' 
                ? 'נסה לשנות את החיפוש או הפילטרים שלך'
                : 'התחל ליצור מחלקות כדי לארגן את הארגון שלך בצורה מקצועית'
              }
            </p>
            {(currentUser?.role === 'org_admin' || currentUser?.role === 'dept_manager') && !searchTerm && filterStatus === 'all' && (
              <button
                onClick={() => {
                  setShowDepartmentModal(true);
                }}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
              >
              <Plus size={20} />
              צור מחלקה ראשונה
            </button>
          )}
        </div>
      )}

      {/* Department Modal */}
      {showDepartmentModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowDepartmentModal(false);
            resetDepartmentForm();
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowDepartmentModal(false);
                  resetDepartmentForm();
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">מחלקה חדשה</h3>
                <p className="text-green-100 text-sm">צור מחלקה חדשה עם כל הפרטים הנדרשים</p>
              </div>
              <button
                type="submit"
                form="newDepartmentForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Plus className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="newDepartmentForm" onSubmit={handleCreateDepartment} className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      שם מחלקה
                    </label>
                    <input
                      type="text"
                      value={departmentForm.name}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                      placeholder="הכנס שם המחלקה"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      תיאור (אופציונלי)
                    </label>
                    <textarea
                      value={departmentForm.description}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all h-24 resize-none"
                      placeholder="תאר את המחלקה בקצרה..."
                      rows="3"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      מנהלי מחלקה (אופציונלי)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-[120px] overflow-y-auto bg-gray-50">
                      {users.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map((user) => (
                        <label key={user.id} className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={departmentForm.managerIds?.includes(user.id) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // הוסף מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: [...(prevState.managerIds || []), user.id]
                                }));
                              } else {
                                // הסר מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: (prevState.managerIds || []).filter(id => id !== user.id)
                                }));
                              }
                            }}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-700">
                            {user.name} ({(() => {
                              switch (user.role) {
                                case 'org_admin':
                                  return 'מנהל ראשי';
                                case 'ceo':
                                  return 'מנכ"ל';
                                case 'deputy_ceo':
                                  return 'סמנכ"ל';
                                case 'dept_manager':
                                  return 'מנהל מחלקה';
                                case 'team_manager':
                                  return 'מנהל צוות';
                                default:
                                  return 'עובד';
                              }
                            })()})
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      לחץ על כל מנהל שברצונך להוסיף
                      <br />
                      <span className="text-blue-600">נבחרו: {departmentForm.managerIds?.length || 0} מנהלים</span>
                    </p>
                  </div>

                 <div>
  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
    <Users className="w-4 h-4 text-gray-500" />
    חברי צוות (אופציונלי)
  </label>
  <EmployeeSearchField
    employees={users.filter(user => user.isActive).map(user => ({
      ...user,
      departmentName: departments.find(d => d.id === user.departmentId)?.name
    }))}
    selectedEmployees={users.filter(user => departmentForm.teamMembers?.includes(user.id))}
    onEmployeeSelect={(employee) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: [...(prevState.teamMembers || []), employee.id]
      }));
    }}
    onEmployeeRemove={(employeeId) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: (prevState.teamMembers || []).filter(id => id !== employeeId)
      }));
    }}
    placeholder="חפש עובדים להוספה למחלקה..."
    showDepartment={true}
  />
</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDepartmentModal(false);
                    resetDepartmentForm();
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  צור מחלקה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {showEditModal && editingDepartment && (
        <div key={`edit-modal-${editingDepartment.id}-${Math.random()}`}>
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditModal(false);
            setEditingDepartment(null);
            resetDepartmentForm();
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingDepartment(null);
                  resetDepartmentForm();
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך מחלקה</h3>
                <p className="text-blue-100 text-sm">עדכן את פרטי המחלקה</p>
              </div>
              <button
                type="submit"
                form="editDepartmentForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editDepartmentForm" onSubmit={handleUpdateDepartment} className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      שם מחלקה
                    </label>
                    <input
                      type="text"
                      value={departmentForm.name}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="הכנס שם המחלקה"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      תיאור (אופציונלי)
                    </label>
                    <textarea
                      value={departmentForm.description}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none"
                      placeholder="תאר את המחלקה בקצרה..."
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={departmentForm.isActive}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">מחלקה פעילה</span>
                    </label>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      מנהלי מחלקה (אופציונלי)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-[120px] overflow-y-auto bg-gray-50">
                      {users.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map((user) => (
                        <label key={user.id} className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={departmentForm.managerIds?.includes(user.id) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // הוסף מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: [...(prevState.managerIds || []), user.id]
                                }));
                              } else {
                                // הסר מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: (prevState.managerIds || []).filter(id => id !== user.id)
                                }));
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {user.name} ({(() => {
                              switch (user.role) {
                                case 'org_admin':
                                  return 'מנהל ראשי';
                                case 'ceo':
                                  return 'מנכ"ל';
                                case 'deputy_ceo':
                                  return 'סמנכ"ל';
                                case 'dept_manager':
                                  return 'מנהל מחלקה';
                                case 'team_manager':
                                  return 'מנהל צוות';
                                default:
                                  return 'עובד';
                              }
                            })()})
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      לחץ על כל מנהל שברצונך להוסיף
                      <br />
                      <span className="text-blue-600">נבחרו: {departmentForm.managerIds?.length || 0} מנהלים</span>
                    </p>
                  </div>

                 <div>
  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
    <Users className="w-4 h-4 text-gray-500" />
    חברי צוות (אופציונלי)
  </label>
  <EmployeeSearchField
    employees={users.filter(user => user.isActive).map(user => ({
      ...user,
      departmentName: departments.find(d => d.id === user.departmentId)?.name
    }))}
    selectedEmployees={users.filter(user => departmentForm.teamMembers?.includes(user.id))}
    onEmployeeSelect={(employee) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: [...(prevState.teamMembers || []), employee.id]
      }));
    }}
    onEmployeeRemove={(employeeId) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: (prevState.teamMembers || []).filter(id => id !== employeeId)
      }));
    }}
    placeholder="חפש עובדים להוספה למחלקה..."
    showDepartment={true}
  />
</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingDepartment(null);
                    resetDepartmentForm();
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  עדכן מחלקה
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}

      {/* Create Department with Teams Modal */}
      {showCreateWithTeams && (
        <CreateDepartmentWithTeams
          onClose={() => setShowCreateWithTeams(false)}
          onSuccess={(data) => {
            console.log('Department created with teams:', data);
            // Optionally refresh the page or update state
            window.location.reload();
          }}
        />
      )}
      </div>
    </div>
  );
};

export default Departments; 